import {
  type InfiniteData,
  useInfiniteQuery,
  useQuery,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type {
  ApiResponse,
  GetClubMembersParams,
  GetMyClubMembershipResponse,
  ClubDetailsDTO,
  ClubEntriesQueryParams,
  ClubEntriesResponseDTO,
  ClubListDTO,
  UserDetailDTO,
  RecentOccupancyEntryDTO,
} from "@membentry/shared-types";
import type { AxiosInstance } from "axios";
import {
  createInfiniteListQuery,
  type InfiniteListOptions,
} from "../common/infinite-list";
import { getNextPageFromMeta } from "../utils/infinite-pagination";
import { CLUB_DAILY_SUMMARY_QUERY_KEY } from "../reception/queryKeys";
import {
  getAllClubsInfiniteQueryKey,
  getClubByIdQueryKey,
  getClubEntriesQueryKey,
  getClubMembersQueryKey,
  getClubMembershipQueryKey,
  getClubsQueryKey,
  getVisibleClubMembersQueryKey,
  getMyClubEntriesQueryKey,
  getMyClubQueryKey,
  getMyClubsInfiniteQueryKey,
  getMyClubsQueryKey,
  getMyClubTodayEntriesQueryKey,
  getTodayClubEntriesQueryKey,
  getMyClubRecentOccupancyQueryKey,
} from "./queryKeys";

export function useGetClubsQuery(
  client: AxiosInstance,
  page = 0,
  pageSize = 10,
  search?: string,
  options?: Omit<
    UseQueryOptions<ApiResponse<ClubListDTO[]>>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: getClubsQueryKey(page, pageSize, search),
    queryFn: async () => {
      const { getAllClubs } = await import("@membentry/shared-api");
      return getAllClubs(client, page, pageSize, search);
    },
    ...options,
  });
}

export function useGetAllClubsInfiniteQuery(
  client: AxiosInstance,
  searchQuery: string,
  options?: Omit<
    UseInfiniteQueryOptions<
      ApiResponse<ClubListDTO[]>,
      Error,
      InfiniteData<ApiResponse<ClubListDTO[]>>,
      ReturnType<typeof getAllClubsInfiniteQueryKey>,
      number
    >,
    "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
  >,
) {
  return useInfiniteQuery<
    ApiResponse<ClubListDTO[]>,
    Error,
    InfiniteData<ApiResponse<ClubListDTO[]>>,
    ReturnType<typeof getAllClubsInfiniteQueryKey>,
    number
  >({
    queryKey: getAllClubsInfiniteQueryKey(searchQuery),
    queryFn: async ({ pageParam = 0 }) => {
      const { getAllClubs } = await import("@membentry/shared-api");
      return getAllClubs(client, pageParam, 10, searchQuery);
    },
    getNextPageParam: (lastPage, allPages) => {
      return getNextPageFromMeta(lastPage, allPages.length, 10);
    },
    initialPageParam: 0,
    ...options,
  });
}

export function useGetClubById(
  client: AxiosInstance,
  id: number | undefined,
  options?: Omit<
    UseQueryOptions<ApiResponse<ClubDetailsDTO>>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: getClubByIdQueryKey(id),
    queryFn: async () => {
      const { getClubById } = await import("@membentry/shared-api");
      return getClubById(client, id!);
    },
    enabled: !!id,
    ...options,
  });
}

export function useGetMyClubQuery(
  client: AxiosInstance,
  options?: Omit<
    UseQueryOptions<ApiResponse<ClubDetailsDTO>>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: getMyClubQueryKey(),
    queryFn: async () => {
      const { getMyClub } = await import("@membentry/shared-api");
      return getMyClub(client);
    },
    ...options,
  });
}

export function useGetMyClubsQuery(
  client: AxiosInstance,
  params?: {
    page?: number;
    pageSize?: number;
    search?: string;
  },
  options?: Omit<
    UseQueryOptions<ApiResponse<ClubListDTO[]>>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: getMyClubsQueryKey(params),
    queryFn: async () => {
      const { getMyClubs } = await import("@membentry/shared-api");
      return getMyClubs(client, params);
    },
    ...options,
  });
}

export function useGetMyClubsInfiniteQuery(
  client: AxiosInstance,
  params?: {
    pageSize?: number;
    search?: string;
  },
  options?: Omit<
    UseInfiniteQueryOptions<
      ApiResponse<ClubListDTO[]>,
      Error,
      InfiniteData<ApiResponse<ClubListDTO[]>>,
      ReturnType<typeof getMyClubsInfiniteQueryKey>,
      number
    >,
    "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
  >,
) {
  const pageSize = params?.pageSize ?? 10;
  const search = params?.search ?? "";

  return useInfiniteQuery<
    ApiResponse<ClubListDTO[]>,
    Error,
    InfiniteData<ApiResponse<ClubListDTO[]>>,
    ReturnType<typeof getMyClubsInfiniteQueryKey>,
    number
  >({
    queryKey: getMyClubsInfiniteQueryKey(pageSize, search),
    queryFn: async ({ pageParam = 0 }) => {
      const { getMyClubs } = await import("@membentry/shared-api");
      return getMyClubs(client, {
        page: pageParam,
        pageSize,
        search,
      });
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageFromMeta(lastPage, allPages.length, pageSize),
    initialPageParam: 0,
    ...options,
  });
}

export const CLUB_MEMBERS_INFINITE_PAGE_SIZE = 20;

export type GetClubMembersInfiniteParams = Omit<GetClubMembersParams, "page">;

const useClubMembersInfiniteList = createInfiniteListQuery<
  GetClubMembersInfiniteParams,
  UserDetailDTO,
  readonly unknown[]
>({
  pageSize: (params) => params.pageSize ?? CLUB_MEMBERS_INFINITE_PAGE_SIZE,
  buildQueryKey: (params, pageSize) =>
    [
      ...getClubMembersQueryKey({ ...params, page: undefined, pageSize }),
      "infinite",
    ] as const,
  fetchPage: async (client, params, { page, pageSize }) => {
    const { getClubMembers } = await import("@membentry/shared-api");
    return getClubMembers(client, { ...params, page, pageSize });
  },
});

export function useGetClubMembersInfiniteQuery(
  client: AxiosInstance,
  params: GetClubMembersInfiniteParams,
  options?: InfiniteListOptions<UserDetailDTO, readonly unknown[]>,
) {
  return useClubMembersInfiniteList(client, params, {
    enabled: !!params.clubId,
    ...options,
  });
}

export function useGetClubMembersQuery(
  client: AxiosInstance,
  params: GetClubMembersParams,
  options?: Omit<
    UseQueryOptions<ApiResponse<UserDetailDTO[]>>,
    "queryKey" | "queryFn"
  >,
) {
  const { clubId } = params;

  return useQuery({
    queryKey: getClubMembersQueryKey(params),
    queryFn: async () => {
      const { getClubMembers } = await import("@membentry/shared-api");
      return getClubMembers(client, params);
    },
    enabled: !!clubId,
    ...options,
  });
}

export function useGetVisibleClubMembersQuery(
  client: AxiosInstance,
  params: GetClubMembersParams,
  options?: Omit<
    UseQueryOptions<ApiResponse<UserDetailDTO[]>>,
    "queryKey" | "queryFn"
  >,
) {
  const { clubId } = params;

  return useQuery({
    queryKey: getVisibleClubMembersQueryKey(params),
    queryFn: async () => {
      const { getVisibleClubMembers } = await import("@membentry/shared-api");
      return getVisibleClubMembers(client, params);
    },
    enabled: !!clubId,
    ...options,
  });
}

export function useGetClubEntriesQuery(
  client: AxiosInstance,
  clubId: number,
  params: Partial<ClubEntriesQueryParams>,
  options?: Omit<
    UseQueryOptions<ApiResponse<ClubEntriesResponseDTO>>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: getClubEntriesQueryKey(clubId, params),
    queryFn: async () => {
      const { getClubEntries } = await import("@membentry/shared-api");
      return getClubEntries(client, clubId, params);
    },
    enabled: !!clubId,
    ...options,
  });
}

export function useGetTodayClubEntriesQuery(
  client: AxiosInstance,
  clubId: number,
  options?: Omit<
    UseQueryOptions<ApiResponse<ClubEntriesResponseDTO>>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: getTodayClubEntriesQueryKey(clubId),
    queryFn: async () => {
      const { getTodayClubEntries } = await import("@membentry/shared-api");
      return getTodayClubEntries(client, clubId);
    },
    enabled: !!clubId,
    ...options,
  });
}

export function useGetMyClubEntriesQuery(
  client: AxiosInstance,
  params: Partial<ClubEntriesQueryParams>,
  options?: Omit<
    UseQueryOptions<ApiResponse<ClubEntriesResponseDTO>>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: getMyClubEntriesQueryKey(params),
    queryFn: async () => {
      const { getMyClubEntries } = await import("@membentry/shared-api");
      return getMyClubEntries(client, params);
    },
    ...options,
  });
}

export function useGetMyClubTodayEntriesQuery(
  client: AxiosInstance,
  params?: Partial<ClubEntriesQueryParams>,
  options?: Omit<UseQueryOptions<ApiResponse<ClubEntriesResponseDTO>>, "queryFn">,
) {
  return useQuery({
    queryKey: options?.queryKey ?? getMyClubTodayEntriesQueryKey(params),
    queryFn: async () => {
      const { getMyClubTodayEntries } = await import("@membentry/shared-api");
      return getMyClubTodayEntries(client, params);
    },
    ...options,
  });
}

export function useGetClubDailySummaryQuery(
  client: AxiosInstance,
  date?: string,
  options?: Omit<UseQueryOptions<import("@membentry/shared-types").GetClubDailySummaryResponse>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: [CLUB_DAILY_SUMMARY_QUERY_KEY, date ?? "today"],
    queryFn: async () => {
      const { getClubDailySummary } = await import("@membentry/shared-api");
      return getClubDailySummary(client, date);
    },
    ...options,
  });
}

export function useGetMyClubRecentOccupancyQuery(
  client: AxiosInstance,
  recentMinutes = 90,
  options?: Omit<UseQueryOptions<ApiResponse<RecentOccupancyEntryDTO[]>>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: getMyClubRecentOccupancyQueryKey(recentMinutes),
    queryFn: async () => {
      const { getMyClubRecentOccupancy } = await import("@membentry/shared-api");
      return getMyClubRecentOccupancy(client, recentMinutes);
    },
    ...options,
  });
}

export function useGetMyClubMembershipQuery(
  client: AxiosInstance,
  clubId: number | undefined,
  options?: Omit<
    UseQueryOptions<GetMyClubMembershipResponse>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: getClubMembershipQueryKey(clubId),
    queryFn: async () => {
      const { getMyClubMembership } = await import("@membentry/shared-api");
      return getMyClubMembership(client, clubId!);
    },
    enabled: !!clubId,
    ...options,
  });
}
